import React, { createContext, FC, useEffect, useState } from 'react';
import { listAppTemplates } from '@wix/ambassador-devcenter-apps-v1-app-template/http';
import { AppTemplate } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import { createHub } from '@wix/fe-essentials/error-monitor';
import * as Sentry from '@sentry/browser';
import { create as createFedOpsLogger } from '@wix/fedops-logger';
import { LoggedInUserRegex } from '../components/TemplatePage/Modals/SelfHostedModal/consts';
import { SENTRY_DSN } from '../constants/sentryDSN';
import { useHttpClient } from '@wix/fe-essentials/react';
import { useTemplatesPageBi } from '../hooks/useTemplatesPageBi';

export interface TemplatesContextProps {
  templates: AppTemplate[];
  isUserLoggedIn: boolean;
  appLoaded: boolean;
}

const fedOpsLogger = createFedOpsLogger('app-templates-page');

export const TemplatesContext = createContext<TemplatesContextProps>({
  templates: [],
  isUserLoggedIn: false,
  appLoaded: false,
});

export const TemplatesContextProvider: FC = ({ children }) => {
  const httpClient = useHttpClient();
  const errorMonitor = createHub({
    dsn: SENTRY_DSN,
    Sentry,
  });

  const { reportMainPageLoaded } = useTemplatesPageBi();
  const [templates, setTemplates] = useState<AppTemplate[]>([]);
  const [appLoaded, setAppLoaded] = React.useState<boolean>(false);

  const loadTemplates = async (): Promise<AppTemplate[]> => {
    const {
      data: { appTemplates },
    } = await httpClient.request(listAppTemplates({}));
    return appTemplates || [];
  };

  useEffect(() => {
    loadTemplates()
      .then((appTemplates) => {
        setTemplates(appTemplates);
      })
      .catch((e) => errorMonitor.captureException(e))
      .finally(() => {
        fedOpsLogger.appLoaded();
        reportMainPageLoaded();
        setAppLoaded(true);
      });
  }, []);

  const isUserLoggedIn = !!document.cookie.match(LoggedInUserRegex);

  return (
    <TemplatesContext.Provider value={{ templates, isUserLoggedIn, appLoaded }}>
      {children}
    </TemplatesContext.Provider>
  );
};
